import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CapacidadService } from '../../core/services/capacidad.service';
import { AlertService } from '../../core/services/alert.service';
import { CapacidadApi, RangoHorarioApi } from '../../core/models/api/empresa-api';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';

@Component({
    selector: 'app-admin-capacidad',
    standalone: true,
    imports: [CommonModule, FormsModule, UiLoadingComponent],
    template: `
    <div class="mb-6">
      <h2 class="text-2xl font-black text-gray-800">Cupos de entrega</h2>
      <p class="text-sm text-gray-400 mt-1">Cada franja tiene un límite. Si ya está llena, súbelo aquí.</p>
    </div>

    <app-ui-loading *ngIf="loading()"></app-ui-loading>

    <section *ngIf="!loading()" class="bg-white rounded-[2rem] p-6 border border-rose-100 shadow-sm mb-6">
      <p class="text-sm font-black text-gray-800 mb-4">Franjas por defecto</p>
      <div class="flex flex-col gap-4">
        <div *ngFor="let r of rangos(); let i = index" class="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <label class="md:col-span-3 block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Nombre</span>
            <input [ngModel]="r.nombre" (ngModelChange)="patchRango(i, { nombre: $event })" [name]="'nom'+i" class="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm" />
          </label>
          <label class="md:col-span-2 block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Inicio</span>
            <input [ngModel]="toInput(r.horaInicio)" (ngModelChange)="patchRango(i, { horaInicio: fromInput($event) })" [name]="'ini'+i" type="time" class="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm" />
          </label>
          <label class="md:col-span-2 block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Fin</span>
            <input [ngModel]="toInput(r.horaFin)" (ngModelChange)="patchRango(i, { horaFin: fromInput($event) })" [name]="'fin'+i" type="time" class="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm" />
          </label>
          <label class="md:col-span-2 block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Cierre pedidos</span>
            <input [ngModel]="toInput(r.horaCierrePedidos)" (ngModelChange)="patchRango(i, { horaCierrePedidos: fromInput($event) })" [name]="'cie'+i" type="time" class="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm" />
          </label>
          <label class="md:col-span-2 block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Cupos default</span>
            <input [ngModel]="r.capacidadDefault" (ngModelChange)="patchRango(i, { capacidadDefault: +$event })" [name]="'cap'+i" type="number" min="0" class="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm" />
          </label>
          <button type="button" (click)="guardarRango(r)" class="md:col-span-1 text-sm font-bold text-rose-500 pb-2">Guardar</button>
        </div>
      </div>
    </section>

    <section *ngIf="!loading()" class="bg-white rounded-[2rem] p-6 border border-rose-100 shadow-sm overflow-x-auto">
      <p class="text-sm font-black text-gray-800 mb-4">Próximos 14 días</p>
      <table class="w-full text-sm min-w-[640px]">
        <thead>
          <tr class="text-left text-xs uppercase tracking-wide text-gray-400">
            <th class="pb-3">Fecha</th>
            <th class="pb-3">Franja</th>
            <th class="pb-3">Usados</th>
            <th class="pb-3">Cupo</th>
            <th class="pb-3"></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let c of capacidades(); let i = index" class="border-t border-gray-50">
            <td class="py-3 font-semibold text-gray-700">{{ c.fecha }}</td>
            <td class="py-3 text-gray-500">{{ c.rangoHorario }}</td>
            <td class="py-3">{{ c.cantidadReservada + c.cantidadConfirmada }}</td>
            <td class="py-3">
              <input [ngModel]="c.capacidadMaxima" (ngModelChange)="patchCap(i, +$event)" [name]="'max'+i" type="number" min="0" class="w-24 rounded-xl border border-gray-200 px-3 py-1.5 text-sm" />
            </td>
            <td class="py-3">
              <button type="button" (click)="guardarCap(c)" class="text-rose-500 font-bold">Actualizar</button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
    `
})
export class AdminCapacidadComponent implements OnInit {
    private capacidadService = inject(CapacidadService);
    private alerts = inject(AlertService);

    loading = signal(true);
    rangos = signal<RangoHorarioApi[]>([]);
    capacidades = signal<CapacidadApi[]>([]);

    ngOnInit(): void {
        const desde = this.iso(new Date());
        const hastaDate = new Date();
        hastaDate.setDate(hastaDate.getDate() + 13);
        const hasta = this.iso(hastaDate);

        this.capacidadService.listRangosHorario().subscribe({
            next: (rangos) => {
                this.rangos.set(rangos);
                this.capacidadService.listCapacidades(desde, hasta).subscribe({
                    next: (rows) => {
                        this.capacidades.set(rows);
                        this.loading.set(false);
                    },
                    error: () => {
                        this.loading.set(false);
                        this.alerts.error('No se pudieron cargar los cupos.');
                    }
                });
            },
            error: () => {
                this.loading.set(false);
                this.alerts.error('No se pudieron cargar las franjas.');
            }
        });
    }

    toInput(value: string) {
        return value ? value.slice(0, 5) : '';
    }

    fromInput(value: string) {
        return value ? (value.length === 5 ? `${value}:00` : value) : '00:00:00';
    }

    patchRango(index: number, partial: Partial<RangoHorarioApi>) {
        this.rangos.update((list) => list.map((item, i) => i === index ? { ...item, ...partial } : item));
    }

    patchCap(index: number, value: number) {
        this.capacidades.update((list) => list.map((item, i) => i === index ? { ...item, capacidadMaxima: value } : item));
    }

    guardarRango(r: RangoHorarioApi) {
        this.capacidadService.updateRangoHorario(r.uuid, {
            nombre: r.nombre,
            horaInicio: r.horaInicio,
            horaFin: r.horaFin,
            horaCierrePedidos: r.horaCierrePedidos,
            capacidadDefault: Number(r.capacidadDefault) || 0,
            orden: r.orden
        }).subscribe({
            next: (ok) => ok ? this.alerts.ok('Franja actualizada') : this.alerts.error('No se pudo guardar la franja'),
            error: () => this.alerts.error('No se pudo guardar la franja')
        });
    }

    guardarCap(c: CapacidadApi) {
        this.capacidadService.updateCapacidad({
            rangoHorarioUuid: c.rangoHorarioUuid,
            fecha: c.fecha.slice(0, 10),
            capacidadMaxima: Number(c.capacidadMaxima) || 0
        }).subscribe({
            next: (ok) => ok ? this.alerts.ok('Cupo actualizado') : this.alerts.error('No se pudo actualizar el cupo'),
            error: () => this.alerts.error('No se pudo actualizar el cupo')
        });
    }

    private iso(date: Date) {
        return date.toISOString().slice(0, 10);
    }
}
